import { useEffect, useMemo, useRef, useState } from 'react';
import CircularStepper from '../../orderTracking/CircularStepper';
import { CustomModal } from '../../ui';
import { CheckCircleFilledIcon, DotCalendarIcon, SpinnerIcon, TrashIcon } from '../../../icons';
import { CalendarIconTracking } from '../../../icons/calendarIconTracking';
import { AddDriverDashboardModal } from '../addDriverDashboardModal';
import { useNavigate } from 'react-router-dom';
import { ToolIcon } from '../../../icons/toolsIcon';
import { Button } from '../../ui/Button';
import { orderSteps } from '../../../config/constants';
import { BookingInitButton } from '../../../pages/delivery/components/BookingInitButton';
import { BookingCancelButton } from '../../../pages/delivery/components/BookingCancelButton';
import usePersonStore from '../../../store/personStore';

export const OrderTrackingTab = ({
  carInfo,
  stepC,
  deliveryInfo,
  deliveryInfoLoading,
  driversList = []
}) => {
  const [otModalOpen, setOtModalOpen] = useState(false)
  const navigate = useNavigate();

  const listRef = useRef(null);
  const assignedRoles = usePersonStore((store) => store.userRoles);
  const hasOnlyDriversRole =
    assignedRoles.length === 1 && assignedRoles.includes('driver');
  
  const scrollToActiveCentered = (behavior = "smooth") => {
    const container = listRef.current;
    if (!container) return;

    const activeEl = container.querySelector(`#step-${stepC}`);
    if (!activeEl) return;

    const targetTop =
      activeEl.offsetTop - container.clientHeight  + activeEl.clientHeight / 2;

    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior,
    });
  };

  const deliveryType = useMemo(()=>{
    if(!!deliveryInfo?.data?.currentlyAllowed){
      return "NEW WAY"
    } else {
      if(deliveryInfo?.data?.deliveryLocation?.garageCategory === "Vehicle Delivery - Επαρχία") {
        return "RAC"
      } else if (deliveryInfo?.data?.deliveryLocation?.garageCategory === "Vehicle Delivery - Dealers") {
        return "AUTHORIZED"
      } else {
        return null
      }
    }
  },[deliveryInfo])

  const availableDate = useMemo(()=>{
    const isAvailDateFromOrderLineValid = carInfo?.orderLine?.AvailDate !== "1900-01-01" && !!carInfo?.orderLine?.AvailDate
    return isAvailDateFromOrderLineValid ? carInfo?.orderLine?.AvailDate : !!deliveryInfo?.data?.availableFrom ? new Date(deliveryInfo?.data?.availableFrom).toISOString().split("T")[0] : undefined
  },[carInfo?.orderLine?.AvailDate, deliveryInfo?.data?.availableFrom])

  const canShowInit = !!deliveryInfo &&
    ((deliveryType === "NEW WAY" && deliveryInfo?.data?.currentlyAllowed === true) || ["AUTHORIZED","RAC"].includes(deliveryType)) &&
    deliveryInfo?.data?.currentlyScheduled !== true &&
    stepC === 7 &&
    carInfo?.hasAssignedDriver === true

  const shouldCheckSpinner = useMemo(() => {
    return stepC === 7 &&!!carInfo?.plateno && (carInfo?.isDeliveryEligible === 1 || ["1006", "1007",].includes(carInfo?.orderLine?.code))
  },[carInfo?.isDeliveryEligible, carInfo?.orderLine?.code, carInfo?.plateno, stepC])
    
  // When modal opens: wait for DOM + layout
  useEffect(() => {
    if (!otModalOpen) return;

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        stepC > 1 && scrollToActiveCentered("smooth"); // "auto" on open feels snappier; change to "smooth" if you want
      });
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [otModalOpen, stepC]);

  // When step changes while modal is open: re-center
  useEffect(() => {
    if (!otModalOpen) return;
    scrollToActiveCentered("smooth");
  }, [stepC, otModalOpen]);

  
  return (
    <div className="flex flex-col">
      <div className='flex flex-col gap-2 justify-center items-center pb-4'>
        {stepC < 9 && <CircularStepper
          step={!!carInfo?.quantity && carInfo?.quantity > 1 ? 4 : stepC}
          showStep={!carInfo?.quantity || (!!carInfo?.quantity && carInfo?.quantity === 1)}
          total={8}
          size={185}
          strokeWidth={5}
          startColor="#590113"
          endColor="#D4002A"
          carInfo={carInfo}
          text={carInfo?.quantity > 1 ? "Σε εξέλιξη" : orderSteps.find(item => item.step === stepC)?.title }
        />}
        {(stepC > 1 && stepC < 7) && carInfo?.orderLine?.DeliverDate !== "1900-01-01" && !!carInfo?.orderLine?.DeliverDate && <div className='flex flex-col gap-[6px] justify-start w-full'>
          <p className='text-sm'>
            Εκτιμώμενη παράδοση
          </p>
          <div className='flex flex-row w-full justify-start gap-2 items-center'>
            <div className='w-6 h-6 rounded-full bg-white flex justify-center items-center'>
              <DotCalendarIcon className={'w-4 h-4'}/>
            </div>
            <p className='text-sm font-bold'>
              {(() => {
                      const [y, m, d] = carInfo?.orderLine.DeliverDate.split("-");
                      return `${d}.${m}.${y}`;
                    })()}
            </p>
          </div>
        </div>}
        {(stepC === 7) && availableDate !== "1900-01-01" && !!availableDate && <div className='flex flex-col gap-[6px] justify-start w-full'>
          <p className='text-sm'>
            Παραλαβή από
          </p>
          <div className='flex flex-row w-full justify-start gap-2 items-center'>
            <div className='w-6 h-6 rounded-full bg-white flex justify-center items-center'>
              <DotCalendarIcon className={'w-4 h-4'}/>
            </div>
            <p className='text-sm font-bold'>
              {(() => {
                      const [y, m, d] = availableDate?.split("-");
                      return `${d}.${m}.${y}`;
                    })()}
            </p>
          </div>
        </div>}
        {/* {stepC === 8 && deliveryInfo?.data?.currentlyScheduled && !!deliveryInfo?.data?.appointment && 
          <div className='flex flex-col justify-start items-start w-full'>
            <p className='text-sm'>
                  Ημ/νία παραλαβής
              </p>
            <div className='flex flex-row gap-2 w-full justify-start items-center'>
              <div className='w-6 h-6 rounded-full bg-white flex justify-center items-center'>
                <CalendarIconTracking className={'w-3 h-3'}/>
              </div>
              <p className='text-sm font-bold'>
                {new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
              </p>
            </div>
            {deliveryInfo?.data?.deliveryFlow !== null && <div className='w-full flex flex-row justify-center'>
              <BookingCancelButton flowId={deliveryInfo?.data?.deliveryFlow?.id} carPlate={carInfo?.plateno ?? carInfo?.vehPlate}/>
            </div>}
          </div>
        } */}

        {stepC === 8 && deliveryInfo?.data?.currentlyScheduled && !!deliveryInfo?.data?.appointment && <div className='flex flex-col gap-[6px] justify-start w-full'>
          <p className='text-sm'>
            Ημ/νία παραλαβής
          </p>
          <div className='flex flex-row w-full justify-between gap-2 items-center'>
            <div className='flex flex-row items-center gap-2'>
              <div className='w-6 h-6 rounded-full bg-white flex justify-center items-center'>
                <DotCalendarIcon className={'w-4 h-4'}/>
              </div>
              <span className='flex flex-row gap-2'>
                <p className='text-sm font-bold'>
                  {new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                </p>
                <p className='text-sm'>
                  {new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleTimeString('el-GR',{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                    timeZone: 'Europe/Athens',
                  })}
                </p>
              </span>
            </div>
            {deliveryInfo?.data?.deliveryFlow !== null && 
              <BookingCancelButton isRetail={true} flowId={deliveryInfo?.data?.deliveryFlow?.id} carPlate={carInfo?.plateno ?? carInfo?.vehPlate } appointment={deliveryInfo?.data?.appointment}/>
            }
          </div>
        </div>
        }
      </div>
      

      {/* New vehicles implementation */}
      {shouldCheckSpinner && deliveryInfoLoading 
      ? <div className='w-full flex justify-center py-2'><SpinnerIcon className="w-4 h-4 fill-primary" /></div> 
      : canShowInit && <>
        <BookingInitButton disabled={false} carInfo={carInfo} deliveryInfo={deliveryInfo?.data}/>
        <div className='h-2 w-full' />
      </>
      }

      {deliveryInfo?.data?.currentlyScheduled && <Button
          className="w-full p-3 text-sm mb-2 text-white font-bold bg-[#D4002A] rounded-md flex gap-2 items-center justify-center"
           onClick={()=>{
              const googleMapsUrl = `https://www.google.com/maps?q=${deliveryInfo?.data?.deliveryLocation?.coordinates?.lat},${deliveryInfo?.data?.deliveryLocation?.coordinates?.lng}`
              window.open(deliveryInfo?.data?.deliveryLocation?.googleMapsURL ?? googleMapsUrl, "_blank", "noopener,noreferrer");           
            }}
        >
          <p className={`${!deliveryInfo?.data?.deliveryLocation?.googleMapsURL && 'text-fadedgrey'}`}>Δες το στον χάρτη</p>
        </Button>}

      {!hasOnlyDriversRole && carInfo?.plateno &&  stepC > 5 && stepC < 9  && !carInfo?.hasAssignedDriver && driversList?.length === 0 &&
        <>
          <AddDriverDashboardModal plateno={carInfo.plateno} text={"Όρισε κύριο οδηγό"}/>
          <div className='h-2 w-full' />
        </>
      }
        
      {stepC === 9 && carInfo?.isDeliveryEligible === 0 && <>
         <p className='text-sm'>
            Το αυτοκίνητό σου είναι έτοιμο για κάθε διαδρομή. Όταν χρειαστείς service, ελαστικά ή φανοποιία, μπορείς να κλείσεις ραντεβού εδώ.
        </p>
        <Button
          className={
            'text-secondary-button bg-white border border-[#A5A5A5] w-full py-3 rounded-[6px] flex gap-1.5 items-center justify-center mt-4'
          }
          onClick={() => {
            navigate('/service-booking');
          }}
        >
          <ToolIcon />
          Ραντεβού Service
        </Button>
      </>}

      {stepC < 9 && <CustomModal
        triggerComponent={
          <div className={`w-full p-3 text-sm text-black font-semibold bg-white border-[1px] border-fadedgrey rounded-md`}>
            Δες λεπτομέρειες
          </div>
        }
        wrapperClass="max-h-[600px] h-[920px]"
        handleClick={setOtModalOpen}
        openModal={otModalOpen}
      >
        <div className="h-full lg:px-10 px-5 pt-5 lg:pt-[40px] lg:pb-7">
          <div className="h-full flex flex-col items-center gap-3 l:gap-6">
            <p className="text-center text-[32px] font-bold text-black">Εξέλιξη παραγγελίας</p>
            <div className='h-full flex flex-row gap-16 justify-center items-center pt-[28px] pb-[38px]'>
              <CircularStepper
                step={!!carInfo?.quantity && carInfo?.quantity > 1 ? 4 : stepC}
                total={8}
                size={303}
                strokeWidth={10}
                startColor="#590113"
                endColor="#D4002A"
                text={orderSteps.find(item => item.step === stepC)?.title}
                carInfo={carInfo}
                showCar={true}
              />
            <div ref={listRef}
              className="h-full flex flex-col gap-3 overflow-auto max-h-[335px] min-w-[444px] pr-9"
            >
              {carInfo?.quantity > 1 ? (
                <div className="h-full flex flex-col gap-4 justify-center items-start">
                  <h3>Σε εξέλιξη</h3>
                  <p className="text-lg">
                    Η παραγγελία σου βρίσκεται σε εξέλιξη. Με την ολοκλήρωση της ταξινόμησης, θα λάβεις νέα ενημέρωση μέσω email.
                  </p>
                  <p className="text-xs text-gray-800 leading-tight pt-2">Ποσότητα παραγγελίας: {carInfo?.quantity} αυτοκίνητα</p>
                </div>
              ) : (
                orderSteps?.map((step) => {
                  const isActive = stepC === step?.step;

                  return isActive ? (
                    <div
                      key={`#step-${step.step}`}
                      id={`step-${step.step}`}
                      className="flex flex-col gap-4 justify-start"
                    >
                      <div className="flex flex-row gap-2 items-center">
                        <p className="text-base text-white bg-black rounded-full px-4 py-[6px]">
                          Βήμα {stepC}
                        </p>
                      </div>

                      <div className="flex flex-row gap-2 items-center">
                        {step?.icon}
                        <p className="text-2xl font-bold text-black">{step?.title}</p>
                      </div>

                      {/* New vehicles implementation - Replace below text */}
                      {/* <p className="text-lg text-black">
                        {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                      </p> */}

                      {/* <p className="text-lg text-black">
                        {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                      </p> */}

                      {stepC === 7 && deliveryInfo?.data?.cancelledButPaid ?
                         <p className="text-lg text-black">Για να προγραμματίσετε εκ νέου το ραντεβού παραλαβής, επικοινωνήστε άμεσα με το Τμήμα Παράδοσης Αυτοκινήτων μέσω e-mail στο car-delivery@avis.gr.</p>
                        : hasOnlyDriversRole && stepC === 7 
                          ? <p className="text-lg text-black">Προγραμμάτισε το ραντεβού για την παραλαβή του αυτοκινήτου σου.</p>
                          : <p className="text-lg text-black">
                              {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                            </p>}


                      {/* {hasOnlyDriversRole && stepC === 7 
                        ? <p className="text-lg text-black">Προγραμμάτισε το ραντεβού για την παραλαβή του αυτοκινήτου σου.</p>
                        : <p className="text-lg text-black">
                            {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                          </p>
                      } */}

                      {stepC === 7 &&
                        availableDate !== null &&
                        availableDate !== undefined &&
                        availableDate !== "1900-01-01" && (
                          <p className="text-xs text-grenite">
                            {`${step?.caption} `}
                            {stepC > 1 &&
                              (() => {
                                const [y, m, d] = availableDate?.split("-");
                                return `${d}/${m}/${y}`;
                              })()}
                          </p>
                        )}

                      {stepC < 7 &&
                        carInfo?.orderLine?.DeliverDate !== null &&
                        carInfo?.orderLine?.DeliverDate !== undefined &&
                        carInfo?.orderLine?.DeliverDate !== "1900-01-01" && (
                          <p className="text-xs text-grenite">
                            {`${step?.caption} `}
                            {stepC > 1 &&
                              (() => {
                                const [y, m, d] = carInfo?.orderLine.DeliverDate.split("-");
                                return `${d}/${m}/${y}`;
                              })()}
                          </p>
                        )}

                      {stepC < 8 && <div className="max-lg:hidden h-[1px] w-full bg-[#D9D9D9]" />}
                    </div>
                  ) : (
                    <div key={step.step} className="flex flex-col">
                      <div className="flex flex-row gap-4 items-center">
                        <div
                          className={`flex justify-center items-center rounded-full h-8 w-8 text-base ${
                            step?.step < stepC ? "bg-[#FEF2F2] text-primary" : "bg-[#F5F5F7]"
                          }`}
                        >
                          {step?.step}
                        </div>

                        {step?.step < stepC && (
                          <CheckCircleFilledIcon className="h-3 w-3 -ml-[25px] -mb-4" />
                        )}

                        <p className={`text-lg ${step?.step > stepC ? "text-[#282828]" : "text-fadedgrey"}`}>
                          {step?.title}
                        </p>
                      </div>

                      <div className="h-[1px] w-full bg-[#D9D9D9] mt-4" />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        </div>
      </CustomModal>}
    </div>
  );
};
